import React, { useEffect, useState } from "react";
import { IconCheck, IconX } from "@tabler/icons-react";
import { getCases, launchExperiment, pollJob } from "../services/api";
import { EMU, EMU_BY_SET, MODEL_SET_OPTIONS, SYSTEMS, couplesWith } from "../constants";
import VendorBadge, { vendorOfSystem } from "../components/VendorBadge";
import CommitLink from "../components/CommitLink";
import { jobHeadline, promptsRefParam } from "../promptsCommit";

// Persona selection: one radio combining the server's style_policy + (for fixed) the single persona.
const PERSONA_OPTIONS = [
  { id: "cycle", label: "Deterministic cycle", hint: "rotates baseline/lower/upper by repeat" },
  { id: "random", label: "Random cycle", hint: "seeded-random persona per run" },
  { id: "baseline", label: "Baseline only" },
  { id: "upper", label: "Upper only" },
  { id: "lower", label: "Lower only" },
];
const FIXED_PERSONAS = ["baseline", "upper", "lower"];

// Mari "mode" (clinic_service_type) — sets the graph preset for Mari runs. Mari-only (the history
// baselines have no graph), but recorded on every run for provenance. Default urgent for Research.
const SERVICE_TYPE_OPTIONS = [
  { id: "urgent", label: "Urgent" },
  { id: "non-urgent", label: "Non-urgent" },
];

export default function RunExperiment() {
  const [cases, setCases] = useState([]);
  const [name, setName] = useState("pilot-experiment");
  const [emu, setEmu] = useState("xai");   // default: Grok — SUT-decoupled, never couples
  const [systems, setSystems] = useState(["mari"]);
  const [caseIds, setCaseIds] = useState([]);
  const [repeats, setRepeats] = useState(3);
  const [persona, setPersona] = useState("cycle");
  const [serviceType, setServiceType] = useState("urgent");   // Mari "mode" — default urgent for Research
  const [modelSet, setModelSet] = useState("current");        // study model set — current | previous
  const [couplingAllowed, setCouplingAllowed] = useState(false);
  // Prompt pinning: optional florence-prompts SHA or branch for the Mari runs. Empty = current main.
  const [promptsRef, setPromptsRef] = useState("");
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { getCases().then((d) => setCases(d.groups.flatMap((g) => g.cases))); }, []);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const total = systems.length * caseIds.length * repeats;
  // Cases list columns: checkbox | chief complaint (fixed 150px) | target diagnosis | vignette snippet.
  // Diagnosis trimmed 20% (1.4->1.12fr) with the total fr held constant (2.6->2.88fr) so the freed width
  // goes to the vignette and the dx column is a true 20% narrower.
  const CASE_COLS = "auto 150px 1.12fr 2.88fr";
  const coupledSystems = systems.filter((s) => couplesWith(s, emu));
  const blocked = coupledSystems.length > 0 && !couplingAllowed;

  const launch = async () => {
    setBusy(true); setJob(null);
    const isFixed = FIXED_PERSONAS.includes(persona);
    // Auto-seed: one fresh seed per launch (recorded with every run for reproducibility). It only
    // feeds the random-persona hash + provenance, so a launch-time value is all it needs to be.
    const seed = `${Date.now()}${Math.floor(Math.random() * 1000)}`;
    try {
      const { job_id } = await launchExperiment({ name, systems, case_ids: caseIds, repeats,
        style_policy: isFixed ? "fixed" : persona, seed, coupling_allowed: couplingAllowed,
        active_styles: isFixed ? [persona] : undefined, service_type: serviceType,
        model_set: modelSet, prompts_ref: promptsRefParam(promptsRef),
        emulator_provider: emu, emulator_model: EMU_BY_SET[modelSet][emu] });
      await pollJob(job_id, { interval: 3000, onTick: setJob });
    } catch (e) { /* surfaced via job */ }
    setBusy(false);
  };

  return (
    <>
      <div className="page-head"><h2>Experiment Setup</h2></div>
      <div className="card">
        <label className="f">Experiment name</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={{ maxWidth: 420 }} />

        <div className="hr" />
        <label className="f">Configuration</label>
        <div className="row" style={{ alignItems: "flex-start" }}>
          {/* History Systems — which systems to drive (Mari + the history-LLM baselines) */}
          <div className="col">
            <label className="f">History Systems</label>
            {SYSTEMS.map((s) => (
              <React.Fragment key={s}>
                <label className="checkline">
                  <input type="checkbox" checked={systems.includes(s)} onChange={() => toggle(systems, setSystems, s)} />
                  <VendorBadge vendor={vendorOfSystem(s)} />
                  {couplesWith(s, emu) && <span className="chip bad">couples with emulator</span>}
                </label>
                {/* Service type — Mari "mode" (clinic_service_type); sets the graph preset. Mari-only, so it
                    lives as a sub-option under the Mari checkbox. Dimmed when Mari isn't selected. */}
                {s === "mari" && (
                  <div style={{ marginLeft: 26, opacity: systems.includes("mari") ? 1 : 0.5 }}>
                    {SERVICE_TYPE_OPTIONS.map((o) => (
                      <label className="checkline" key={o.id} style={{ fontSize: 13 }}>
                        <input type="radio" name="service_type" checked={serviceType === o.id}
                               disabled={!systems.includes("mari")} onChange={() => setServiceType(o.id)} />
                        <span>{o.label}</span>
                      </label>
                    ))}
                    {/* Prompts commit — every Mari run of the experiment uses this ONE florence-prompts
                        commit (resolved at launch). Empty = the commit current on main. */}
                    <label className="f" htmlFor="prompts-ref" style={{ marginTop: 6 }}>Prompts commit</label>
                    <input id="prompts-ref" type="text" className="mono" value={promptsRef}
                           placeholder="current main" disabled={!systems.includes("mari")}
                           onChange={(e) => setPromptsRef(e.target.value)} style={{ width: 180 }} />
                    <div className="muted" style={{ fontSize: 11.5, marginTop: 3, maxWidth: 200 }}>
                      SHA or branch. Empty = current main.
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Emulator — single select over the cheapest per-vendor emulator model */}
          <div className="col">
            <label className="f">Emulator</label>
            {Object.keys(EMU).map((p) => (
              <label className="checkline" key={p}>
                <input type="radio" name="emulator" checked={emu === p} onChange={() => setEmu(p)} />
                <VendorBadge vendor={p} />
              </label>
            ))}
          </div>

          {/* Persona — single select combining style_policy + (for fixed) the one persona */}
          <div className="col">
            <label className="f">Persona</label>
            {PERSONA_OPTIONS.map((o) => (
              <label className="checkline" key={o.id}>
                <input type="radio" name="persona" checked={persona === o.id} onChange={() => setPersona(o.id)} />
                <span>{o.label}{o.hint && <span className="muted"> ({o.hint})</span>}</span>
              </label>
            ))}
          </div>

          {/* Model set — which frozen study set the history SUTs + emulator run (resolved server-side).
              Option B: Mari's internal runtime models and the evaluator ALWAYS run current. */}
          <div className="col">
            <label className="f">Model set</label>
            {MODEL_SET_OPTIONS.map((o) => (
              <label className="checkline" key={o.id}>
                <input type="radio" name="model_set" checked={modelSet === o.id} onChange={() => setModelSet(o.id)} />
                <span>{o.label}</span>
              </label>
            ))}
            {modelSet === "previous" && (
              <div className="muted" style={{ fontSize: 12, marginTop: 4, maxWidth: 230 }}>
                Applies to the history baselines and the emulator. Mari runs its current runtime
                models; the evaluator stays current.
              </div>
            )}
          </div>
        </div>

        <div className="hr" />
        <label className="f">Cases</label>
        <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid var(--line)", borderRadius: 8, padding: 8 }}>
          {cases.map((c) => (
            <label className="checkline" key={c.id}
                   style={{ display: "grid", gridTemplateColumns: CASE_COLS, gap: 10, alignItems: "start" }}>
              <input type="checkbox" checked={caseIds.includes(c.id)} onChange={() => toggle(caseIds, setCaseIds, c.id)} />
              <span className="mono">{c.chief_complaint}</span>
              <span>{(c.ground_truth_differential && c.ground_truth_differential[0]) || <span className="muted">—</span>}</span>
              <span className="muted">{(c.vignette || "").slice(0, 120)}{(c.vignette || "").length > 120 ? "…" : ""}</span>
            </label>
          ))}
          {cases.length === 0 && <span className="muted">No cases — add some in the Case Library.</span>}
        </div>

        <div className="hr" />
        <label className="f">Repeats</label>
        <input type="number" min="1" value={repeats}
          onChange={(e) => setRepeats(parseInt(e.target.value || "0", 10))}
          style={{ width: 140, fontSize: 22, fontWeight: 600, padding: "10px 14px" }} />
        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>Seed is auto-generated per launch and recorded with each run.</div>

        {coupledSystems.length > 0 && (
          <label className="checkline" style={{ marginTop: 10 }}>
            <input type="checkbox" checked={couplingAllowed} onChange={(e) => setCouplingAllowed(e.target.checked)} />
            Allow model coupling (exploratory) — coupled runs are tagged &amp; excluded from the confirmatory pool
          </label>
        )}
        {blocked && <div className="banner bad" style={{ marginTop: 10 }}>Anti-coupling: {coupledSystems.join(", ")} share the emulator's family. Enable the exploratory override or drop those systems.</div>}

        <div className="matrix">
          <div className="mtile"><b>{systems.length}</b><small>systems</small></div>
          <div className="mtile"><b>{caseIds.length}</b><small>cases</small></div>
          <div className="mtile"><b>{repeats}</b><small>repeats</small></div>
          <div className="mtile" style={{ borderColor: "var(--accent)", background: "var(--accent-l)" }}><b>{total}</b><small>runs</small></div>
        </div>

        <div className="between" style={{ marginTop: 16 }}>
          <span className="muted">Queue-managed · persisted with full provenance.</span>
          <button className="btn primary" disabled={busy || blocked || total === 0} onClick={launch}>Launch experiment →</button>
        </div>
      </div>

      {job && (
        <div className="card">
          <h3 className="flex" style={{ gap: 8 }}>{jobHeadline(job)}
            {job.prompts_commit && <span className="chip n" title="All Mari runs use this florence-prompts commit">
              prompts <CommitLink sha={job.prompts_commit} icon={false} /></span>}
          </h3>
          {job.status === "preparing" && (
            <div className="muted" style={{ fontSize: 12.5 }}>
              Pinning florence-prompts <span className="mono">{job.prompts_ref || "main"}</span>. Runs start when the prompt set is loaded.
            </div>
          )}
          {job.status === "error" && job.error && <div className="banner bad" style={{ marginTop: 6 }}>{job.error}</div>}
          <div className="mono">{(job.results || []).map((r, i) =>
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>{r.error
              ? <><IconX size={13} style={{ color: "var(--bad)" }} /> {r.error} ({r.system})</>
              : <><IconCheck size={13} style={{ color: "var(--ok)" }} /> {r.summary?.system} · run {(r.run_id || "").slice(0, 8)}</>}</div>)}</div>
        </div>
      )}
    </>
  );
}
